import React, { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCheckIcon, CreditCardIcon, ShipIcon, FileTextIcon, CheckCircleIcon, PlayIcon, StopCircleIcon, BarChart3Icon, ArrowRightIcon } from 'lucide-react';
export function UserFlow() {
  const navigate = useNavigate();
  const steps = [{
    id: 1,
    title: 'Add Beneficiary',
    description: 'Register new resource collector',
    icon: <UserCheckIcon size={32} data-id="element-2742" />,
    role: 'Station Officer',
    path: '/beneficiaries',
    status: 'completed' as const
  }, {
    id: 2,
    title: 'Approve Beneficiary',
    description: 'Review and approve registration',
    icon: <CheckCircleIcon size={32} data-id="element-2743" />,
    role: 'Range Officer',
    path: '/beneficiaries',
    status: 'completed' as const
  }, {
    id: 3,
    title: 'Issue Smart ID',
    description: 'Generate QR/RFID card',
    icon: <CreditCardIcon size={32} data-id="element-2744" />,
    role: 'Station Officer',
    path: '/smart-id',
    status: 'completed' as const
  }, {
    id: 4,
    title: 'Register Vessel',
    description: 'Register boat (if applicable)',
    icon: <ShipIcon size={32} data-id="element-2745" />,
    role: 'Station Officer',
    path: '/vessels',
    status: 'current' as const
  }, {
    id: 5,
    title: 'Apply Permit',
    description: 'Submit forest entry permit',
    icon: <FileTextIcon size={32} data-id="element-2746" />,
    role: 'Beneficiary/Officer',
    path: '/permits',
    status: 'upcoming' as const
  }, {
    id: 6,
    title: 'Approve Permit',
    description: 'Review and approve permit',
    icon: <CheckCircleIcon size={32} data-id="element-2747" />,
    role: 'Range Officer',
    path: '/permits',
    status: 'upcoming' as const
  }, {
    id: 7,
    title: 'Start Trip',
    description: 'QR/RFID scan at entry',
    icon: <PlayIcon size={32} data-id="element-2748" />,
    role: 'Field Officer',
    path: '/permits',
    status: 'upcoming' as const
  }, {
    id: 8,
    title: 'End Trip',
    description: 'QR/RFID scan at exit',
    icon: <StopCircleIcon size={32} data-id="element-2749" />,
    role: 'Field Officer',
    path: '/permits',
    status: 'upcoming' as const
  }, {
    id: 9,
    title: 'Monitoring & Reporting',
    description: 'Track and analyze data',
    icon: <BarChart3Icon size={32} data-id="element-2750" />,
    role: 'HQ Admin',
    path: '/reports',
    status: 'upcoming' as const
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 border-green-500 text-green-900';
      case 'current':
        return 'bg-blue-100 border-blue-500 text-blue-900';
      case 'upcoming':
        return 'bg-gray-100 border-gray-300 text-gray-600';
      default:
        return 'bg-gray-100 border-gray-300 text-gray-600';
    }
  };
  return <div className="space-y-6" data-id="element-2751">
      <div data-id="element-2752">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2753">Complete User Flow</h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-2754">
          End-to-end journey from registration to monitoring
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2755">
        <p className="text-sm text-blue-900" data-id="element-2756">
          <strong data-id="element-2757">How to use:</strong> This diagram shows the complete workflow
          from beneficiary registration to trip monitoring. Click on any step to
          navigate to that module.
        </p>
      </div>

      {/* Horizontal Flow */}
      <div className="bg-white rounded-lg border border-gray-200 p-8 overflow-x-auto" data-id="element-2758">
        <div className="flex items-center gap-4 min-w-max" data-id="element-2759">
          {steps.map((step, idx) => <Fragment key={step.id} data-id="element-2760">
              <button onClick={() => navigate(step.path)} className={`flex-shrink-0 w-64 border-2 rounded-lg p-6 text-left transition-all hover:shadow-lg ${getStatusColor(step.status)}`} data-id="element-2761">
                <div className="flex items-start gap-4 mb-3" data-id="element-2762">
                  <div className={`p-3 rounded-lg ${step.status === 'completed' ? 'bg-green-200' : step.status === 'current' ? 'bg-blue-200' : 'bg-gray-200'}`} data-id="element-2763">
                    {step.icon}
                  </div>
                  <div className="flex-1" data-id="element-2764">
                    <div className="text-xs font-semibold uppercase mb-1" data-id="element-2765">
                      Step {step.id}
                    </div>
                    <h3 className="font-bold text-lg mb-1" data-id="element-2766">{step.title}</h3>
                  </div>
                </div>
                <p className="text-sm mb-3" data-id="element-2767">{step.description}</p>
                <div className="flex items-center justify-between" data-id="element-2768">
                  <span className="text-xs font-medium" data-id="element-2769">{step.role}</span>
                  {step.status === 'completed' && <CheckCircleIcon size={20} className="text-green-600" data-id="element-2770" />}
                  {step.status === 'current' && <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" data-id="element-2771"></div>}
                </div>
              </button>
              {idx < steps.length - 1 && <ArrowRightIcon className="flex-shrink-0 text-gray-400" size={32} data-id="element-2772" />}
            </Fragment>)}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2773">
        <h3 className="font-semibold text-gray-900 mb-4" data-id="element-2774">Status Legend</h3>
        <div className="flex gap-6" data-id="element-2775">
          <div className="flex items-center gap-2" data-id="element-2776">
            <div className="w-4 h-4 bg-green-500 rounded" data-id="element-2777"></div>
            <span className="text-sm text-gray-700" data-id="element-2778">Completed</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-2779">
            <div className="w-4 h-4 bg-blue-500 rounded" data-id="element-2780"></div>
            <span className="text-sm text-gray-700" data-id="element-2781">Current Step</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-2782">
            <div className="w-4 h-4 bg-gray-300 rounded" data-id="element-2783"></div>
            <span className="text-sm text-gray-700" data-id="element-2784">Upcoming</span>
          </div>
        </div>
      </div>
    </div>;
}